import { sumPlatformQueryCredits } from '@lensora/shared'
import { Button, Label, Spinner, cn } from '@lensora/ui'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  Plus, Upload, Search, X, ChevronDown, Trash2,
  Download, RotateCw, CheckCircle2, Loader2, Info,
  BarChart2, ArrowUpDown, Sparkles, TrendingUp, MessageCircle,
} from 'lucide-react'
import * as React from 'react'
import { NavLink } from 'react-router-dom'

import { FaviconImg } from '@/components/dashboard/FaviconImg'
import { PlatformIcon, formatPlatformName, isGrowthGatedPlatform, PICKER_PLATFORMS } from '@/components/dashboard/PlatformIcon'
import { PlatformResponseBody } from '@/components/dashboard/PlatformResponseBody'
import { useActiveWebsite } from '@/hooks/use-active-website'
import { useCommunityPrompts, useRefreshCommunityPrompts } from '@/hooks/use-community-prompts'
import { useGrowthPlus } from '@/hooks/use-growth-plus'
import { useTrackAndRunAll, TrackAndRunAllError } from '@/hooks/use-track-and-run-all'
import { useTrendingPrompts, useRefreshTrendingPrompts } from '@/hooks/use-trending-queries'
import { api, ApiError } from '@/lib/api'
import { filterMentionPills } from '@/lib/mentions'

// ── Types ─────────────────────────────────────────────────────────────────────

interface PromptResult {
  id: string
  query_text: string
  platform: string
  brand_mentioned: boolean
  mention_position: number | null
  sentiment: string | null
  competitor_mentions: string[]
  response_text: string
  created_at: string
  tags?: string[] | null
  ai_mentioned_brands?: string[]
}

interface TrackedPrompt {
  query_text: string
  all_ids: string[]
  platforms_scanned: string[]
  platforms_mentioned: string[]
  visibility: number
  latest_created_at: string
  tags: string[]
  results: PromptResult[]
  status?: 'running' | 'error'
}

// ── Constants ─────────────────────────────────────────────────────────────────

// Non-Growth+ default selection — everything in PICKER_PLATFORMS except
// platforms gated by isGrowthGatedPlatform.
const DEFAULT_PLATFORMS = PICKER_PLATFORMS.filter((p) => !isGrowthGatedPlatform(p))
const TAG_PRESETS = ['Brand', 'Product', 'Corporate', 'Competitor', 'Industry', 'Other']

// ── Volume ────────────────────────────────────────────────────────────────────

type VolumeTier = 'high' | 'medium' | 'low'

function estimateVolume(prompt: string): VolumeTier {
  const words = prompt.trim().split(/\s+/).length
  const generic = /best|top|how to|what is|compare|\bvs\b|review/i.test(prompt)
  if (generic && words <= 8) return 'high'
  if (words <= 12) return 'medium'
  return 'low'
}

function VolumeBadge({ prompt }: { prompt: string }) {
  const tier = estimateVolume(prompt)
  const label: Record<VolumeTier, string> = { high: 'High', medium: 'Med', low: 'Low' }
  const color: Record<VolumeTier, string> = {
    high: 'text-feedback-success',
    medium: 'text-feedback-warning',
    low: 'text-text-muted',
  }
  return (
    <span className={cn('shrink-0 text-xs', color[tier])}>· {label[tier]}</span>
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function relativeTime(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hours = Math.floor(mins / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return `${Math.floor(days / 7)}w ago`
}

function visibilityColor(v: number): string {
  if (v < 0) return 'text-text-muted'
  if (v < 50) return 'text-feedback-error'
  if (v < 80) return 'text-feedback-warning'
  return 'text-feedback-success'
}

function visibilityDotColor(v: number): string {
  if (v < 0) return 'bg-border'
  if (v < 50) return 'bg-feedback-error'
  if (v < 80) return 'bg-feedback-warning'
  return 'bg-feedback-success'
}

function groupResults(rows: PromptResult[]): TrackedPrompt[] {
  const map: Record<string, PromptResult[]> = {}
  for (const row of rows) {
    if (!map[row.query_text]) map[row.query_text] = []
    map[row.query_text]!.push(row)
  }
  return Object.entries(map)
    .map(([query_text, rowGroup]) => {
      const real = rowGroup.filter((r) => r.platform !== 'manual')
      const platforms_scanned = [...new Set(real.map((r) => r.platform))]
      const platforms_mentioned = real.filter((r) => r.brand_mentioned).map((r) => r.platform)
      const visibility = real.length > 0
        ? Math.min(100, Math.round((real.filter((r) => r.brand_mentioned).length / real.length) * 100))
        : -1
      const latest_created_at = rowGroup.reduce(
        (max, r) => (r.created_at > max ? r.created_at : max),
        rowGroup[0]?.created_at ?? ''
      )
      const tags = rowGroup.find((r) => r.tags && r.tags.length > 0)?.tags ?? []
      return {
        query_text, all_ids: rowGroup.map((r) => r.id),
        platforms_scanned, platforms_mentioned, visibility,
        latest_created_at, tags,
        results: rowGroup.sort((a, b) => b.created_at.localeCompare(a.created_at)),
      }
    })
    .sort((a, b) => b.latest_created_at.localeCompare(a.latest_created_at))
}

function exportToCSV(prompts: TrackedPrompt[]) {
  const headers = ['Prompt', 'Visibility%', 'Platforms Scanned', 'Platforms Mentioned', 'Tags', 'Last Run']
  const lines = prompts.map((p) => [
    `"${p.query_text.replace(/"/g, '""')}"`,
    p.visibility >= 0 ? `${p.visibility}%` : 'N/A',
    p.platforms_scanned.join('; '),
    p.platforms_mentioned.join('; '),
    `"${p.tags.join('; ')}"`,
    new Date(p.latest_created_at).toISOString().split('T')[0] ?? '',
  ])
  const csv = [headers, ...lines].map((r) => r.join(',')).join('\n')
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
  const a = document.createElement('a')
  a.href = url
  a.download = `lensora-prompts-${new Date().toISOString().split('T')[0]}.csv`
  a.click()
  URL.revokeObjectURL(url)
}

// ── AI Mention Pills ───────────────────────────────────────────────────────────

function MentionPills({
  brands,
  trackedBrands,
  onTrack,
  tracking,
}: {
  brands: string[]
  trackedBrands: Set<string>
  onTrack: (name: string) => void
  tracking: string | null
}) {
  const pills = filterMentionPills(brands)
  if (pills.length === 0) return null
  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-border pt-3">
      <span className="text-[11px] text-text-muted">AI mentioned:</span>
      {pills.map((brand) => {
        const isTracked = trackedBrands.has(brand.toLowerCase())
        const isTracking = tracking === brand
        const domain = brand.toLowerCase().replace(/[^a-z0-9.]/g, '') + (brand.includes('.') ? '' : '.com')

        if (isTracked) {
          return (
            <span key={brand} className="inline-flex items-center gap-1 rounded-full border border-feedback-success/30 bg-feedback-success/10 px-2 py-0.5">
              <CheckCircle2 className="h-3 w-3 shrink-0 text-feedback-success" />
              <span className="text-[11px] text-feedback-success">{brand}</span>
            </span>
          )
        }
        if (isTracking) {
          return (
            <span key={brand} className="inline-flex items-center gap-1 rounded-full border border-brand-accent/30 bg-brand-accent/10 px-2 py-0.5">
              <Loader2 className="h-3 w-3 shrink-0 animate-spin text-brand-accent" />
              <span className="text-[11px] text-text-muted">{brand}</span>
            </span>
          )
        }
        return (
          <button
            key={brand}
            title="Click to track this competitor"
            onClick={() => onTrack(brand)}
            className="inline-flex items-center gap-1 rounded-full border border-border bg-surface px-2 py-0.5 transition-colors hover:border-brand-accent/40"
          >
            <FaviconImg domain={domain} name={brand} size={12} />
            <span className="text-[11px] text-text-muted">{brand}</span>
          </button>
        )
      })}
    </div>
  )
}

// ── Platform Response Card ─────────────────────────────────────────────────────

function PlatformCard({
  result,
  trackedBrands,
  onTrack,
  tracking,
  brandName,
}: {
  result: PromptResult
  trackedBrands: Set<string>
  onTrack: (name: string) => void
  tracking: string | null
  brandName?: string | undefined
}) {
  const allBrands = filterMentionPills([
    ...(result.ai_mentioned_brands ?? []),
    ...(result.competitor_mentions ?? []),
  ])

  return (
    <div className="rounded-lg border border-border bg-surface-raised p-4">
      <div className="mb-3 flex items-center gap-2">
        <PlatformIcon platform={result.platform} size={18} dimmed={!result.brand_mentioned} />
        <span className="text-xs font-semibold text-text">{formatPlatformName(result.platform)}</span>
        <span className="text-[11px] text-text-muted">{relativeTime(result.created_at)}</span>
        <div className="ml-auto">
          {result.brand_mentioned ? (
            <span className="rounded-full border border-feedback-success/30 bg-feedback-success/10 px-2 py-0.5 text-[10px] font-medium text-feedback-success">
              ✓ Mentioned{result.mention_position !== null ? ` #${result.mention_position}` : ''}
            </span>
          ) : (
            <span className="rounded-full border border-border px-2 py-0.5 text-[10px] text-text-muted">
              Not mentioned
            </span>
          )}
        </div>
      </div>

      <PlatformResponseBody
        text={result.response_text}
        brandName={brandName}
        brandMentioned={result.brand_mentioned}
      />

      <MentionPills
        brands={allBrands}
        trackedBrands={trackedBrands}
        onTrack={onTrack}
        tracking={tracking}
      />
    </div>
  )
}

// ── Prompt Card ───────────────────────────────────────────────────────────────

function PromptCard({
  prompt,
  checked,
  onCheck,
  onRunAgain,
  running,
  trackedBrands,
  onTrack,
  tracking,
  brandName,
  onDelete,
  websiteId: _websiteId,
}: {
  prompt: TrackedPrompt
  checked: boolean
  onCheck: (c: boolean) => void
  onRunAgain: (q: string) => void
  running: boolean
  trackedBrands: Set<string>
  onTrack: (name: string) => void
  tracking: string | null
  brandName?: string | undefined
  onDelete: () => void
  websiteId?: string | null | undefined
}) {
  const [expanded, setExpanded] = React.useState(false)
  const platformResults = PICKER_PLATFORMS
    .filter((p) => prompt.platforms_scanned.includes(p))
    .map((p) => prompt.results.filter((r) => r.platform === p).sort((a, b) => b.created_at.localeCompare(a.created_at))[0])
    .filter((r): r is PromptResult => r !== undefined)

  const isRunning = prompt.status === 'running'

  return (
    <div className={cn('overflow-hidden rounded-lg border bg-surface transition-colors', expanded ? 'border-brand-accent/30' : 'border-border')}>
      {/* Header */}
      <div
        className="group flex min-h-[56px] cursor-pointer select-none items-center gap-3 px-4 py-3 hover:bg-surface-raised"
        onClick={() => setExpanded((e) => !e)}
      >
        <div onClick={(e) => e.stopPropagation()} className="shrink-0">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => { e.stopPropagation(); onCheck(e.target.checked) }}
            onClick={(e) => e.stopPropagation()}
            className="h-4 w-4 rounded border-border accent-brand-accent"
          />
        </div>

        <p className="min-w-0 flex-1 truncate text-sm text-text">{prompt.query_text}</p>

        {/* Visibility */}
        <div className="flex shrink-0 items-center gap-1.5">
          {isRunning ? (
            <Loader2 className="h-3 w-3 animate-spin text-brand-accent" />
          ) : (
            <div className={cn('h-2 w-2 shrink-0 rounded-full', visibilityDotColor(prompt.visibility))} />
          )}
          <span className={cn('text-sm font-medium tabular-nums', isRunning ? 'text-brand-accent' : visibilityColor(prompt.visibility))}>
            {isRunning ? 'Scanning…' : prompt.visibility < 0 ? '—' : `${prompt.visibility}%`}
          </span>
        </div>

        {/* Platform icons */}
        <div className="flex shrink-0 items-center gap-1">
          {PICKER_PLATFORMS.map((p) => {
            const scanned = prompt.platforms_scanned.includes(p)
            const mentioned = prompt.platforms_mentioned.includes(p)
            if (!scanned && prompt.platforms_scanned.length > 0) return null
            return (
              <PlatformIcon
                key={p}
                platform={p}
                size={16}
                dimmed={scanned ? !mentioned : true}
              />
            )
          })}
        </div>

        {/* Volume */}
        <VolumeBadge prompt={prompt.query_text} />

        {/* Timestamp */}
        <span className="shrink-0 text-xs text-text-muted">{relativeTime(prompt.latest_created_at)}</span>

        {/* Re-run */}
        <button
          onClick={(e) => { e.stopPropagation(); onRunAgain(prompt.query_text) }}
          disabled={running || isRunning}
          title="Run again"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-accent/10 text-brand-accent transition-colors hover:bg-brand-accent/20 disabled:cursor-not-allowed disabled:opacity-30"
        >
          <RotateCw className="h-3 w-3" />
        </button>

        {/* Delete — reveal on hover */}
        <button
          onClick={(e) => { e.stopPropagation(); onDelete() }}
          title="Delete"
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-feedback-error/10 text-feedback-error opacity-0 transition-all hover:bg-feedback-error/20 group-hover:opacity-100"
        >
          <Trash2 className="h-3 w-3" />
        </button>

        <ChevronDown className={cn('h-4 w-4 shrink-0 text-text-muted transition-transform', expanded && 'rotate-180')} />
      </div>

      {/* Expanded detail */}
      {expanded && (
        <div className="border-t border-border bg-surface-raised/50 p-4">
          {isRunning ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {[0, 1].map((i) => (
                <div key={i} className="h-24 animate-pulse rounded-lg bg-surface-overlay" />
              ))}
            </div>
          ) : platformResults.length === 0 ? (
            <div className="flex items-center gap-2 text-sm text-text-muted">
              <Info className="h-3.5 w-3.5 shrink-0" />
              Added manually — no simulation run yet. Use the run bar above to get visibility data.
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
              {platformResults.map((r) => (
                <PlatformCard
                  key={r.id}
                  result={r}
                  trackedBrands={trackedBrands}
                  onTrack={onTrack}
                  tracking={tracking}
                  brandName={brandName}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ── Add Prompt Modal ──────────────────────────────────────────────────────────

function AddPromptModal({ onClose, onAdded }: { onClose: () => void; onAdded: () => void }) {
  const [text, setText] = React.useState('')
  const [tag, setTag] = React.useState('')
  const [customTag, setCustomTag] = React.useState('')
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState('')

  const effectiveTag = tag === '__custom__' ? customTag.trim() : tag

  async function handleAdd() {
    if (!text.trim()) { setError('Prompt text is required'); return }
    setSaving(true); setError('')
    try {
      await api.post('/api/prompts', { query_text: text.trim(), tags: effectiveTag ? [effectiveTag] : [] })
      onAdded(); onClose()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to add prompt')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="w-full max-w-md rounded-lg border border-border bg-surface p-6 shadow-lg">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-base font-semibold text-text">Add Prompt Manually</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="prompt-text">Prompt text</Label>
            <textarea
              id="prompt-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="e.g. What is the best AI visibility monitoring tool?"
              rows={3}
              autoFocus
              className="w-full resize-none rounded-lg border border-border bg-surface-raised px-3.5 py-3 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !saving) { e.preventDefault(); void handleAdd() } }}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="prompt-tag">Category / tag</Label>
            <select
              id="prompt-tag"
              value={tag}
              onChange={(e) => setTag(e.target.value)}
              className="w-full rounded-lg border border-border bg-surface-raised px-3.5 py-2.5 text-sm text-text focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
            >
              <option value="">No tag</option>
              {TAG_PRESETS.map((t) => <option key={t} value={t}>{t}</option>)}
              <option value="__custom__">Custom…</option>
            </select>
            {tag === '__custom__' && (
              <input
                value={customTag}
                onChange={(e) => setCustomTag(e.target.value)}
                placeholder="Enter custom tag"
                autoFocus
                className="mt-2 w-full rounded-lg border border-border bg-surface-raised px-3.5 py-2.5 text-sm text-text placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
              />
            )}
          </div>
          <div className="rounded-lg border border-feedback-info/20 bg-feedback-info/5 px-3.5 py-2.5">
            <p className="text-xs text-feedback-info">
              This adds the prompt to your tracked list without running a simulation. Use the run bar on the prompts page to get visibility data.
            </p>
          </div>
          {error && <p className="text-sm text-feedback-error">{error}</p>}
          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={() => void handleAdd()} disabled={saving || !text.trim()}>
              {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Adding…</> : <><Plus className="mr-2 h-4 w-4" />Add prompt</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Bulk Import Modal ─────────────────────────────────────────────────────────

interface CsvRow { text: string; tag: string }

function BulkImportModal({ onClose, onImported }: { onClose: () => void; onImported: () => void }) {
  const [rows, setRows] = React.useState<CsvRow[]>([])
  const [progress, setProgress] = React.useState(0)
  const [importing, setImporting] = React.useState(false)
  const [done, setDone] = React.useState(false)
  const [error, setError] = React.useState('')
  const fileRef = React.useRef<HTMLInputElement>(null)

  function handleFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      const text = ev.target?.result as string
      const lines = text.split('\n').map((l) => l.trim()).filter(Boolean)
      const data = lines[0]?.toLowerCase().includes('prompt') ? lines.slice(1) : lines
      setRows(
        data
          .map((line) => {
            const parts = line.split(',').map((p) => p.trim().replace(/^"|"$/g, ''))
            return { text: parts[0] ?? '', tag: parts[1] ?? '' }
          })
          .filter((r) => r.text.length > 0)
      )
    }
    reader.readAsText(file)
  }

  async function handleImport() {
    if (rows.length === 0) { setError('No prompts loaded'); return }
    setError(''); setImporting(true); setProgress(0)
    try {
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]!
        await api.post('/api/prompts', { query_text: row.text, tags: row.tag ? [row.tag] : [] })
        setProgress(i + 1)
      }
      setDone(true)
      setTimeout(() => { onImported(); onClose() }, 800)
    } catch {
      setError('Import failed — please try again')
    } finally {
      setImporting(false)
    }
  }

  const preview = rows.slice(0, 5)

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
    >
      <div className="w-full max-w-lg rounded-lg border border-border bg-surface p-6 shadow-lg">
        <div className="mb-5 flex items-center justify-between">
          <h2 className="text-base font-semibold text-text">Bulk Import CSV</h2>
          <button onClick={onClose} className="text-text-muted hover:text-text"><X className="h-4 w-4" /></button>
        </div>
        <div className="space-y-4">
          <div className="rounded-lg border border-border bg-surface-raised px-3.5 py-3">
            <p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-text-muted">Expected format</p>
            <p className="font-mono text-xs leading-relaxed text-text-muted">
              prompt text,Tag (optional)<br />
              What is the best CRM?,Brand<br />
              Compare marketing tools,Product
            </p>
          </div>

          <button
            onClick={() => fileRef.current?.click()}
            className="w-full rounded-lg border-2 border-dashed border-border py-8 text-center transition-colors hover:border-brand-accent/40"
          >
            <Upload className="mx-auto mb-2 h-5 w-5 text-text-muted" />
            <p className="text-xs text-text-muted">
              {rows.length > 0 ? `${rows.length} prompt${rows.length !== 1 ? 's' : ''} ready to import` : 'Click to upload CSV'}
            </p>
          </button>
          <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={handleFile} />

          {preview.length > 0 && (
            <div>
              <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                Preview — first {preview.length} row{preview.length !== 1 ? 's' : ''}
              </p>
              <div className="overflow-hidden rounded-lg border border-border">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-text-muted">Prompt</th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wider text-text-muted">Tag</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {preview.map((r, i) => (
                      <tr key={i}>
                        <td className="max-w-[280px] truncate px-3 py-2 text-text">{r.text}</td>
                        <td className="px-3 py-2">
                          {r.tag
                            ? <span className="rounded-full bg-brand-accent/10 px-1.5 py-0.5 text-[10px] text-brand-accent">{r.tag}</span>
                            : <span className="text-text-muted">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {rows.length > 5 && <p className="mt-1 text-[10px] text-text-muted">+{rows.length - 5} more rows</p>}
            </div>
          )}

          {importing && (
            <div>
              <div className="mb-1 flex justify-between text-[10px] text-text-muted">
                <span>Importing…</span><span>{progress} / {rows.length}</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-surface-overlay">
                <div
                  className="h-full rounded-full bg-brand-accent transition-all duration-300"
                  style={{ width: `${(progress / rows.length) * 100}%` }}
                />
              </div>
            </div>
          )}

          {error && <p className="text-sm text-feedback-error">{error}</p>}
          {done && <p className="text-sm text-feedback-success">Import complete! Refreshing…</p>}

          <div className="flex justify-end gap-2 pt-1">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={() => void handleImport()} disabled={importing || rows.length === 0 || done}>
              {importing
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Importing {progress}/{rows.length}…</>
                : <><Upload className="mr-2 h-4 w-4" />Import {rows.length} prompt{rows.length !== 1 ? 's' : ''}</>
              }
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

// ── Delete Confirm ────────────────────────────────────────────────────────────

function DeleteConfirmModal({
  count, onConfirm, onCancel, deleting,
}: { count: number; onConfirm: () => void; onCancel: () => void; deleting: boolean }) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onCancel() }}
    >
      <div className="w-full max-w-sm rounded-lg border border-feedback-error/30 bg-surface p-6 shadow-lg">
        <h2 className="mb-2 text-base font-semibold text-text">
          Delete {count} prompt{count !== 1 ? 's' : ''}?
        </h2>
        <p className="mb-5 text-sm text-text-muted">
          This permanently removes all scan data for the selected prompts. This cannot be undone.
        </p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onCancel} disabled={deleting}>Cancel</Button>
          <Button variant="destructive" onClick={onConfirm} disabled={deleting}>
            {deleting ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Deleting…</> : <><Trash2 className="mr-2 h-4 w-4" />Delete</>}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ── Suggested Prompts ─────────────────────────────────────────────────────────

const REFRESH_ERROR_MESSAGES: Record<string, string> = {
  INSUFFICIENT_CREDITS: 'Not enough credits to refresh right now.',
  NOT_FOUND: 'No website selected — add a website first.',
  RATE_LIMITED: 'Too many refreshes — try again in a bit.',
  DAILY_CAP_REACHED: 'Daily discovery limit reached for today.',
}

function usedSuggestionsKey(websiteId: string | null) {
  return `lensora_used_suggestions_${websiteId ?? 'none'}`
}

function loadUsedSuggestions(websiteId: string | null): Set<string> {
  try {
    const raw = localStorage.getItem(usedSuggestionsKey(websiteId))
    return raw ? new Set(JSON.parse(raw) as string[]) : new Set()
  } catch {
    return new Set()
  }
}

function saveUsedSuggestions(websiteId: string | null, used: Set<string>) {
  try {
    localStorage.setItem(usedSuggestionsKey(websiteId), JSON.stringify([...used]))
  } catch {
    // localStorage unavailable — used-state just won't persist across reloads
  }
}

function ForYouTab({
  websiteId,
  suggestions,
  used,
  onSelect,
}: {
  websiteId: string | null
  suggestions: string[]
  used: Set<string>
  onSelect: (text: string) => void
}) {
  const queryClient = useQueryClient()
  const [refreshError, setRefreshError] = React.useState<string | null>(null)

  const refreshMutation = useMutation({
    mutationFn: () => api.post<{ suggestions: string[] }>('/api/suggest-prompts', { website_id: websiteId }),
    onMutate: () => setRefreshError(null),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['websites'] })
    },
    onError: (error: unknown) => {
      if (error instanceof ApiError) {
        const messages: Record<string, string> = {
          INSUFFICIENT_CREDITS: 'Not enough credits to generate more suggestions.',
          NOT_FOUND: 'No website selected — add a website first.',
        }
        setRefreshError(messages[error.code ?? ''] ?? error.message)
      } else {
        setRefreshError('Could not generate suggestions. Please try again.')
      }
    },
  })

  return (
    <>
      <DiscoveryTabHeader
        copy="AI-suggested prompts based on your website and industry."
        refreshedAt={null}
        stale={false}
        canRefresh={!!websiteId}
        refreshing={refreshMutation.isPending}
        onRefresh={() => refreshMutation.mutate()}
        refreshLabel="Generate more"
        disabledTitle="Add a website first"
      />
      {refreshError && (
        <p className="border-b border-border bg-feedback-error/10 px-3 py-2 text-[11px] text-feedback-error">
          {refreshError}
        </p>
      )}
      <DiscoveryPromptList
        prompts={suggestions.map((s) => ({ queryText: s, sourceUrls: [] }))}
        used={used}
        loading={false}
        showSources={false}
        emptyText={refreshMutation.isPending ? 'Generating suggestions…' : 'No suggestions yet — click "Generate more".'}
        onSelect={onSelect}
      />
    </>
  )
}

function DiscoveryTabHeader({
  copy,
  refreshedAt,
  stale,
  canRefresh,
  refreshing,
  onRefresh,
  refreshLabel = 'Refresh',
  disabledTitle = 'Live search is unavailable right now',
}: {
  copy: string
  refreshedAt: string | null
  stale: boolean
  canRefresh: boolean
  refreshing: boolean
  onRefresh: () => void
  refreshLabel?: string
  disabledTitle?: string
}) {
  return (
    <div className="border-b border-border px-3 py-2">
      <div className="flex items-start justify-between gap-2">
        <p className="text-[11px] leading-snug text-text-muted">{copy}</p>
        <button
          onClick={onRefresh}
          disabled={refreshing || !canRefresh}
          title={!canRefresh ? disabledTitle : undefined}
          className="flex shrink-0 items-center gap-1 text-[11px] font-medium text-brand-accent hover:underline disabled:cursor-not-allowed disabled:opacity-50"
        >
          {refreshing
            ? <Loader2 className="h-3 w-3 animate-spin" />
            : <RotateCw className="h-3 w-3" />}
          {refreshLabel}
        </button>
      </div>
      {stale && refreshedAt && (
        <p className="mt-1 text-[10px] text-text-muted">Last updated {relativeTime(refreshedAt)}</p>
      )}
    </div>
  )
}

function DiscoveryPromptList({
  prompts,
  used,
  loading,
  emptyText,
  onSelect,
  showSources,
}: {
  prompts: { queryText: string; sourceUrls: string[] }[]
  used: Set<string>
  loading: boolean
  emptyText: string
  onSelect: (text: string) => void
  showSources: boolean
}) {
  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="h-4 w-4 animate-spin text-text-muted" />
      </div>
    )
  }
  if (prompts.length === 0) {
    return <p className="px-3 py-4 text-center text-xs text-text-muted">{emptyText}</p>
  }
  return (
    <div className="max-h-64 overflow-y-auto py-1">
      {prompts.map((p) => (
        <div key={p.queryText} className="flex w-full items-start gap-2 px-3 py-2 text-left text-xs transition-colors hover:bg-surface-raised">
          {used.has(p.queryText) && <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-feedback-success" />}
          <div className="min-w-0 flex-1">
            <span className={cn('text-text', used.has(p.queryText) && 'text-text-muted line-through')}>{p.queryText}</span>
            {showSources && p.sourceUrls.length > 0 && (
              <p className="mt-0.5 text-[10px] text-text-muted">{p.sourceUrls.join(', ')}</p>
            )}
          </div>
          <button
            onClick={() => onSelect(p.queryText)}
            className="shrink-0 text-[11px] font-medium text-brand-accent hover:underline"
          >
            Use →
          </button>
        </div>
      ))}
    </div>
  )
}

function TrackAndRunAllBar({
  websiteId,
  queryTexts,
  platforms,
}: {
  websiteId: string | null
  queryTexts: string[]
  platforms: string[]
}) {
  const [armed, setArmed] = React.useState(false)
  const [addError, setAddError] = React.useState<string | null>(null)
  const [partialFailure, setPartialFailure] = React.useState<string | null>(null)
  const [runFailures, setRunFailures] = React.useState<{ platform: string; error: string }[]>([])
  const armTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const { data: creditsData } = useQuery({
    queryKey: ['credits-balance'],
    queryFn: () => api.get<{ balance: number }>('/api/credits/balance'),
  })
  const balance = creditsData?.balance ?? null

  // Cost must be derived the exact same way the server debits it, so the
  // displayed number can never drift from what actually gets charged.
  const cost = queryTexts.length * sumPlatformQueryCredits(platforms)
  const shortfall = balance !== null && balance < cost ? cost - balance : 0

  const mutation = useTrackAndRunAll(websiteId)

  function handleClick() {
    if (!armed) {
      setArmed(true)
      armTimeoutRef.current = setTimeout(() => setArmed(false), 5000)
      return
    }
    if (armTimeoutRef.current) clearTimeout(armTimeoutRef.current)
    setArmed(false)
    setAddError(null)
    setPartialFailure(null)
    setRunFailures([])
    mutation.mutate(
      { queryTexts, platforms },
      {
        onSuccess: (data) => setRunFailures(data.failures ?? []),
        onError: (error: unknown) => {
          if (error instanceof TrackAndRunAllError && error.stage === 'run') {
            setPartialFailure(`Added ${error.addedCount} prompts, but the run failed — retry from the run bar.`)
          } else {
            setAddError('Could not add prompts. Please try again.')
          }
        },
      }
    )
  }

  React.useEffect(() => () => { if (armTimeoutRef.current) clearTimeout(armTimeoutRef.current) }, [])

  return (
    <div className="border-t border-border px-3 py-2.5">
      <Button
        size="sm"
        onClick={handleClick}
        disabled={mutation.isPending || (shortfall > 0 && !armed)}
        className="w-full"
      >
        {mutation.isPending
          ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Running…</>
          : armed
            ? `Confirm — track ${queryTexts.length} prompt${queryTexts.length !== 1 ? 's' : ''} and run across ${platforms.length} platform${platforms.length !== 1 ? 's' : ''} for ${cost} credits?`
            : `Track & Run All — ${cost} credits`}
      </Button>
      {shortfall > 0 && (
        <p className="mt-1.5 text-[11px] text-feedback-error">Need {shortfall} more credits.</p>
      )}
      {addError && <p className="mt-1.5 text-[11px] text-feedback-error">{addError}</p>}
      {partialFailure && <p className="mt-1.5 text-[11px] text-feedback-error">{partialFailure}</p>}
      {runFailures.length > 0 && (
        <div className="mt-1.5 text-[11px] text-feedback-error">
          <p className="font-semibold">{runFailures.length} platform{runFailures.length !== 1 ? 's' : ''} failed to run:</p>
          <ul className="mt-0.5 space-y-0.5 text-text-muted">
            {runFailures.map((f) => (
              <li key={f.platform}><span className="font-medium text-text">{formatPlatformName(f.platform)}</span>: {f.error}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

function TrendingTab({
  websiteId,
  brandName,
  open,
  used,
  onSelect,
  selectedPlatforms,
}: {
  websiteId: string | null
  brandName?: string | undefined
  open: boolean
  used: Set<string>
  onSelect: (text: string) => void
  selectedPlatforms: string[]
}) {
  const { data, isLoading } = useTrendingPrompts(websiteId, open)
  const refreshMutation = useRefreshTrendingPrompts(websiteId)
  const [refreshError, setRefreshError] = React.useState<string | null>(null)

  function handleRefresh() {
    setRefreshError(null)
    refreshMutation.mutate(undefined, {
      onError: (error: unknown) => {
        setRefreshError(
          error instanceof ApiError ? (REFRESH_ERROR_MESSAGES[error.code ?? ''] ?? error.message) : 'Could not refresh trending prompts. Please try again.'
        )
      },
    })
  }

  const prompts = data?.prompts ?? []

  return (
    <>
      <DiscoveryTabHeader
        copy="What buyers are asking AI about in your industry. Track individually or run all at once."
        refreshedAt={data?.refreshedAt ?? null}
        stale={data?.stale ?? false}
        canRefresh={data?.canRefresh ?? false}
        refreshing={refreshMutation.isPending}
        onRefresh={handleRefresh}
      />
      {refreshError && (
        <p className="border-b border-border bg-feedback-error/10 px-3 py-2 text-[11px] text-feedback-error">{refreshError}</p>
      )}
      <DiscoveryPromptList
        prompts={prompts}
        used={used}
        loading={isLoading}
        showSources
        emptyText={`No trending prompts yet — hit Refresh to search the web for what buyers are asking about ${brandName ?? 'your brand'}`}
        onSelect={onSelect}
      />
      {prompts.length > 0 && (
        <TrackAndRunAllBar
          websiteId={websiteId}
          queryTexts={prompts.map((p) => p.queryText)}
          platforms={selectedPlatforms}
        />
      )}
    </>
  )
}

function CommunityTab({
  websiteId,
  open,
  used,
  onSelect,
}: {
  websiteId: string | null
  open: boolean
  used: Set<string>
  onSelect: (text: string) => void
}) {
  const { data, isLoading } = useCommunityPrompts(websiteId, open)
  const refreshMutation = useRefreshCommunityPrompts(websiteId)
  const [refreshError, setRefreshError] = React.useState<string | null>(null)

  function handleRefresh() {
    setRefreshError(null)
    refreshMutation.mutate(undefined, {
      onError: (error: unknown) => {
        setRefreshError(
          error instanceof ApiError ? (REFRESH_ERROR_MESSAGES[error.code ?? ''] ?? error.message) : 'Could not refresh community prompts. Please try again.'
        )
      },
    })
  }

  const prompts = data?.prompts ?? []

  return (
    <>
      <DiscoveryTabHeader
        copy="Prompts that surface community discussions, reviews and social proof — drives UGC & Reference citations."
        refreshedAt={data?.refreshedAt ?? null}
        stale={data?.stale ?? false}
        canRefresh={data?.canRefresh ?? true}
        refreshing={refreshMutation.isPending}
        onRefresh={handleRefresh}
      />
      {refreshError && (
        <p className="border-b border-border bg-feedback-error/10 px-3 py-2 text-[11px] text-feedback-error">{refreshError}</p>
      )}
      <DiscoveryPromptList
        prompts={prompts}
        used={used}
        loading={isLoading}
        showSources={false}
        emptyText="No community prompts yet — hit Refresh to find discussions and reviews."
        onSelect={onSelect}
      />
    </>
  )
}

function SuggestedPromptsButton({
  websiteId,
  brandName,
  suggestions,
  onSelect,
  selectedPlatforms,
}: {
  websiteId: string | null
  brandName?: string | undefined
  suggestions: string[]
  onSelect: (text: string) => void
  selectedPlatforms: string[]
}) {
  const [open, setOpen] = React.useState(false)
  const [activeTab, setActiveTab] = React.useState<'for-you' | 'trending' | 'community'>('for-you')
  const [used, setUsed] = React.useState<Set<string>>(() => loadUsedSuggestions(websiteId))

  React.useEffect(() => {
    setUsed(loadUsedSuggestions(websiteId))
  }, [websiteId])

  function handleSelect(text: string) {
    onSelect(text)
    setUsed((prev) => {
      const next = new Set(prev).add(text)
      saveUsedSuggestions(websiteId, next)
      return next
    })
    setOpen(false)
  }

  const TABS = [
    { id: 'for-you' as const, label: 'For You', icon: Sparkles },
    { id: 'trending' as const, label: 'Trending', icon: TrendingUp },
    { id: 'community' as const, label: 'Community', icon: MessageCircle },
  ]

  return (
    <div className="relative shrink-0">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-1.5 rounded-md border border-border bg-surface-raised px-2.5 py-1.5 text-xs font-medium text-text transition-colors hover:border-brand-accent/40"
      >
        <Sparkles className="h-3.5 w-3.5 text-brand-accent" />
        Suggested
      </button>
      {open && (
        <div className="absolute bottom-full left-0 z-20 mb-2 w-96 overflow-hidden rounded-lg border border-border bg-surface shadow-lg">
          <div className="flex gap-1 border-b border-border bg-surface-raised p-1">
            {TABS.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex flex-1 items-center justify-center gap-1 rounded-md px-2 py-1.5 text-[11px] font-semibold transition-colors',
                    activeTab === tab.id ? 'bg-surface text-text shadow-sm' : 'text-text-muted hover:text-text'
                  )}
                >
                  <Icon className="h-3 w-3" />
                  {tab.label}
                </button>
              )
            })}
          </div>

          {activeTab === 'for-you' && (
            <ForYouTab websiteId={websiteId} suggestions={suggestions} used={used} onSelect={handleSelect} />
          )}
          {activeTab === 'trending' && (
            <TrendingTab
              websiteId={websiteId}
              brandName={brandName}
              open={open && activeTab === 'trending'}
              used={used}
              onSelect={handleSelect}
              selectedPlatforms={selectedPlatforms}
            />
          )}
          {activeTab === 'community' && (
            <CommunityTab websiteId={websiteId} open={open && activeTab === 'community'} used={used} onSelect={handleSelect} />
          )}
        </div>
      )}
    </div>
  )
}

// ── Run Bar ───────────────────────────────────────────────────────────────────

function RunBar({
  onSubmit,
  running,
  suggestedPrompts,
  websiteId,
  brandName,
}: {
  onSubmit: (query: string, platforms: string[]) => void
  running: boolean
  suggestedPrompts: string[]
  websiteId: string | null
  brandName?: string | undefined
}) {
  const [input, setInput] = React.useState('')
  const [selectedPlatforms, setSelectedPlatforms] = React.useState<string[]>([...DEFAULT_PLATFORMS])
  const [showPlatforms, setShowPlatforms] = React.useState(false)
  const [spellNotice, setSpellNotice] = React.useState<{ original: string; corrected: string } | null>(null)
  const isGrowthPlus = useGrowthPlus()

  function togglePlatform(p: string) {
    if (isGrowthGatedPlatform(p) && !isGrowthPlus) return
    setSelectedPlatforms((prev) => {
      const next = prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p]
      return next.length > 0 ? next : prev
    })
  }

  async function handleSubmit() {
    if (!input.trim() || running) return
    const original = input.trim()
    setInput('')

    // Silent spell correction — never blocks the run on failure
    let finalText = original
    try {
      const { corrected } = await api.post<{ corrected: string }>('/api/spell-check', { text: original })
      if (corrected && corrected.toLowerCase().trim() !== original.toLowerCase().trim()) {
        finalText = corrected
        setSpellNotice({ original, corrected })
        setTimeout(() => setSpellNotice(null), 4000)
      }
    } catch {
      // Silent fallback — use original text
    }

    onSubmit(finalText, selectedPlatforms)
  }

  return (
    <div className="relative rounded-xl p-px shadow-lg" style={{ background: 'linear-gradient(135deg, var(--brand-accent), var(--brand-primary))' }}>
      {/* Spell correction notice */}
      <p
        className={cn(
          'pointer-events-none absolute -top-6 left-1 text-xs text-text-muted transition-opacity duration-300',
          spellNotice ? 'opacity-100' : 'opacity-0'
        )}
      >
        {spellNotice && (
          <>Auto-corrected: &ldquo;{spellNotice.original}&rdquo; → &ldquo;{spellNotice.corrected}&rdquo;</>
        )}
      </p>
      <div className="flex items-center gap-2 rounded-[11px] bg-surface px-3 py-2.5">
        {/* Suggested prompts — left side */}
        <SuggestedPromptsButton
          websiteId={websiteId}
          brandName={brandName}
          suggestions={suggestedPrompts}
          onSelect={(text) => setInput(text)}
          selectedPlatforms={selectedPlatforms}
        />

        <div className="h-4 w-px shrink-0 bg-border" />

        {/* Platform toggle */}
        <div className="relative shrink-0">
          <button
            onClick={() => setShowPlatforms((s) => !s)}
            className="flex items-center gap-1.5 rounded-md border border-border bg-surface-raised px-2.5 py-1.5 text-xs font-medium text-text transition-colors hover:border-brand-accent/40"
          >
            <BarChart2 className="h-3.5 w-3.5 text-brand-accent" />
            {selectedPlatforms.length === PICKER_PLATFORMS.length ? `${PICKER_PLATFORMS.length} platforms` : `${selectedPlatforms.length} platform${selectedPlatforms.length !== 1 ? 's' : ''}`}
            <ChevronDown className="h-3 w-3 text-text-muted" />
          </button>
          {showPlatforms && (
            <div className="absolute bottom-full left-0 z-20 mb-2 w-48 overflow-hidden rounded-lg border border-border bg-surface shadow-lg">
              <p className="border-b border-border px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-text-muted">Select platforms</p>
              <div className="py-1">
                {PICKER_PLATFORMS.map((p) => {
                  const locked = isGrowthGatedPlatform(p) && !isGrowthPlus
                  return (
                    <button
                      key={p}
                      onClick={() => togglePlatform(p)}
                      disabled={locked}
                      className={cn(
                        'flex w-full items-center gap-3 px-3 py-2 text-left transition-colors',
                        locked ? 'cursor-not-allowed opacity-50' : 'hover:bg-surface-raised'
                      )}
                    >
                      <div className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border transition-colors',
                        selectedPlatforms.includes(p) ? 'border-brand-accent bg-brand-accent' : 'border-border'
                      )}>
                        {selectedPlatforms.includes(p) && (
                          <svg className="h-2.5 w-2.5 text-text-inverse" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        )}
                      </div>
                      <PlatformIcon platform={p} size={14} />
                      <span className="flex-1 text-xs text-text">{formatPlatformName(p)}</span>
                      {locked && (
                        <span className="rounded-full border border-brand-accent/30 bg-brand-accent/10 px-1.5 py-0.5 text-[10px] text-brand-accent">
                          Growth+
                        </span>
                      )}
                    </button>
                  )
                })}
              </div>
              <div className="border-t border-border px-3 py-2">
                <button onClick={() => setShowPlatforms(false)} className="text-[10px] font-medium text-brand-accent hover:underline">Done</button>
              </div>
            </div>
          )}
        </div>

        <div className="h-4 w-px shrink-0 bg-border" />

        <Search className="h-4 w-4 shrink-0 text-text-muted" />
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder="Ask anything about your AI visibility…"
          className="min-w-0 flex-1 bg-transparent text-sm text-text placeholder:text-text-muted focus:outline-none"
          onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit() }}
          disabled={running}
        />
        {input && !running && (
          <button onClick={() => setInput('')} className="shrink-0 text-text-muted hover:text-text">
            <X className="h-3.5 w-3.5" />
          </button>
        )}
        <Button
          size="sm"
          onClick={handleSubmit}
          disabled={!input.trim() || running}
          className="shrink-0"
        >
          {running ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Running…</> : 'Run Simulation →'}
        </Button>
      </div>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────

export function PromptsPage() {
  const queryClient = useQueryClient()
  const [platformFilter, setPlatformFilter] = React.useState('All')
  const [mentionedFilter, setMentionedFilter] = React.useState<'all' | 'yes' | 'no'>('all')
  const [volumeFilter, setVolumeFilter] = React.useState<'all' | VolumeTier>('all')
  const [volumeSort, setVolumeSort] = React.useState<'asc' | 'desc' | null>(null)
  const [sortDesc, setSortDesc] = React.useState(true)
  const [checked, setChecked] = React.useState<Set<string>>(new Set())
  const [showAdd, setShowAdd] = React.useState(false)
  const [showImport, setShowImport] = React.useState(false)
  const [showDeleteConfirm, setShowDeleteConfirm] = React.useState(false)
  const [runningQueries, setRunningQueries] = React.useState<Set<string>>(new Set())
  const [trackedBrands, setTrackedBrands] = React.useState<Set<string>>(new Set())
  const [tracking, setTracking] = React.useState<string | null>(null)
  const [globalRunning, setGlobalRunning] = React.useState(false)
  const [runFailures, setRunFailures] = React.useState<{ platform: string; error: string }[]>([])

  // Data
  const { website: activeWebsite, websiteId } = useActiveWebsite()

  const { data: resultsData, isLoading } = useQuery({
    queryKey: ['simulate-results', websiteId],
    queryFn: () => api.get<{ results: PromptResult[] }>(`/api/simulate/results?limit=500&website_id=${websiteId}`),
    enabled: !!websiteId,
    staleTime: 30_000,
  })

  const brandName = activeWebsite?.name ?? undefined

  const allPrompts = React.useMemo(
    () => groupResults(resultsData?.results ?? []).map((p): TrackedPrompt => {
      if (runningQueries.has(p.query_text)) return { ...p, status: 'running' }
      const { status, ...rest } = p
      return status !== undefined ? { ...rest, status } : rest
    }),
    [resultsData, runningQueries]
  )

  const TIER_ORDER: Record<VolumeTier, number> = { high: 3, medium: 2, low: 1 }

  const filtered = React.useMemo(() => {
    return allPrompts
      .filter((p) => {
        if (platformFilter !== 'All' && !p.platforms_scanned.includes(platformFilter)) return false
        if (mentionedFilter === 'yes' && p.visibility <= 0) return false
        if (mentionedFilter === 'no' && p.visibility > 0) return false
        if (volumeFilter !== 'all' && estimateVolume(p.query_text) !== volumeFilter) return false
        return true
      })
      .sort((a, b) => {
        if (volumeSort !== null) {
          const aScore = TIER_ORDER[estimateVolume(a.query_text)]
          const bScore = TIER_ORDER[estimateVolume(b.query_text)]
          if (aScore !== bScore) return volumeSort === 'desc' ? bScore - aScore : aScore - bScore
        }
        return sortDesc
          ? b.latest_created_at.localeCompare(a.latest_created_at)
          : a.latest_created_at.localeCompare(b.latest_created_at)
      })
  }, [allPrompts, platformFilter, mentionedFilter, volumeFilter, volumeSort, sortDesc])

  // Mutations
  const runMutation = useMutation({
    mutationFn: ({ queries, platforms }: { queries: { query_text: string }[]; platforms: string[] }) =>
      api.post<{ failures: { platform: string; error: string }[] }>('/api/simulate', {
        queries,
        platforms,
        website_id: websiteId,
      }),
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['simulate-results'] })
      setRunningQueries(new Set())
      setGlobalRunning(false)
      setRunFailures(data.failures ?? [])
    },
    onError: () => {
      setRunningQueries(new Set())
      setGlobalRunning(false)
    },
  })

  function handleRun(query: string, platforms: string[]) {
    setRunFailures([])
    setRunningQueries((prev) => new Set([...prev, query]))
    setGlobalRunning(true)
    runMutation.mutate({ queries: [{ query_text: query }], platforms })
  }

  const deleteMutation = useMutation({
    mutationFn: (queryTexts: string[]) =>
      api.post('/api/prompts/bulk-delete', { query_texts: queryTexts }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['simulate-results'] })
      setChecked(new Set())
      setShowDeleteConfirm(false)
    },
  })

  function handleDeleteSelected() {
    deleteMutation.mutate([...checked])
  }

  const trackMutation = useMutation({
    mutationFn: (name: string) => {
      const domain = name.toLowerCase().replace(/[^a-z0-9.]/g, '') + (name.includes('.') ? '' : '.com')
      return api.post('/api/competitors', { website_id: websiteId, name, url: `https://${domain}` })
    },
    onSuccess: (_, name) => {
      setTrackedBrands((prev) => new Set([...prev, name.toLowerCase()]))
      setTracking(null)
    },
    onError: () => setTracking(null),
  })

  function handleTrack(name: string) {
    setTracking(name)
    trackMutation.mutate(name)
  }

  const allChecked = filtered.length > 0 && filtered.every((p) => checked.has(p.query_text))
  function toggleAll() {
    if (allChecked) {
      setChecked((prev) => { const next = new Set(prev); filtered.forEach((p) => next.delete(p.query_text)); return next })
    } else {
      setChecked((prev) => new Set([...prev, ...filtered.map((p) => p.query_text)]))
    }
  }
  return (
    <div className="space-y-5 pb-28">
      {/* Header */}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-text">Tracked Prompts</h2>
          <p className="mt-1 text-sm text-text-muted">Monitor where your brand appears across AI platforms</p>
          <p className="mt-0.5 text-[11px] italic text-text-muted opacity-60">
            AI responses vary — run the same prompt multiple times for a reliable visibility score.
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {/* Page tab nav */}
          <div className="flex items-center rounded-lg border border-border bg-surface-raised p-0.5">
            <NavLink
              to="/dashboard/prompts"
              end
              className={({ isActive }: { isActive: boolean }) => cn(
                'flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition-colors',
                isActive ? 'bg-brand-primary text-brand-primary-foreground shadow-sm' : 'text-text-muted hover:text-text'
              )}
            >
              <Search className="h-3.5 w-3.5" />Prompts
            </NavLink>
            <NavLink
              to="/dashboard/competitors"
              className={({ isActive }: { isActive: boolean }) => cn(
                'flex items-center gap-1.5 rounded px-3 py-1.5 text-xs font-medium transition-colors',
                isActive ? 'bg-brand-primary text-brand-primary-foreground shadow-sm' : 'text-text-muted hover:text-text'
              )}
            >
              Competitors
            </NavLink>
          </div>
          <Button size="sm" onClick={() => setShowAdd(true)}>
            <Plus className="mr-1.5 h-3.5 w-3.5" />Add
          </Button>
          <Button variant="outline" size="sm" onClick={() => setShowImport(true)}>
            <Upload className="mr-1.5 h-3.5 w-3.5" />Import CSV
          </Button>
        </div>
      </div>

      {runFailures.length > 0 && (
        <div className="rounded-lg border border-feedback-error/30 bg-feedback-error/5 px-4 py-3 text-xs">
          <p className="font-semibold text-feedback-error">
            {runFailures.length === 1 ? '1 platform' : `${runFailures.length} platforms`} failed to run:
          </p>
          <ul className="mt-1 space-y-0.5 text-text-muted">
            {runFailures.map((f) => (
              <li key={f.platform}>
                <span className="font-medium text-text">
                  {formatPlatformName(f.platform)}
                </span>
                : {f.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={platformFilter}
          onChange={(e) => setPlatformFilter(e.target.value)}
          className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-text focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
        >
          <option value="All">All Platforms</option>
          {PICKER_PLATFORMS.map((p) => <option key={p} value={p}>{formatPlatformName(p)}</option>)}
        </select>

        <select
          value={mentionedFilter}
          onChange={(e) => setMentionedFilter(e.target.value as 'all' | 'yes' | 'no')}
          className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-text focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
        >
          <option value="all">All Results</option>
          <option value="yes">Mentioned</option>
          <option value="no">Not Mentioned</option>
        </select>

        <select
          value={volumeFilter}
          onChange={(e) => setVolumeFilter(e.target.value as 'all' | VolumeTier)}
          className="rounded-lg border border-border bg-surface px-2.5 py-1.5 text-xs text-text focus:outline-none focus:ring-2 focus:ring-brand-accent/40"
        >
          <option value="all">All Volumes</option>
          <option value="high">High Volume</option>
          <option value="medium">Medium Volume</option>
          <option value="low">Low Volume</option>
        </select>

        <button
          onClick={() => setVolumeSort((s) => s === 'desc' ? 'asc' : s === 'asc' ? null : 'desc')}
          className={cn(
            'flex items-center gap-1 rounded-lg border px-2.5 py-1.5 text-xs transition-colors',
            volumeSort !== null
              ? 'border-brand-accent/40 text-brand-accent'
              : 'border-border text-text-muted hover:border-border-strong hover:text-text'
          )}
          title="Sort by estimated search volume"
        >
          Vol <ArrowUpDown className="h-3 w-3" />
        </button>

        <button
          onClick={() => setSortDesc((d) => !d)}
          className="rounded-lg border border-border px-2.5 py-1.5 text-xs text-text-muted transition-colors hover:border-border-strong hover:text-text"
        >
          {sortDesc ? 'Newest first' : 'Oldest first'}
        </button>

        <div className="ml-auto flex items-center gap-3">
          {filtered.length > 0 && (
            <button
              onClick={toggleAll}
              className="text-xs text-text-muted transition-colors hover:text-text"
            >
              {allChecked ? 'Deselect all' : 'Select all'}
            </button>
          )}
          <span className="text-xs text-text-muted">
            {filtered.length} prompt{filtered.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Bulk action bar */}
      {checked.size > 0 && (
        <div className="flex items-center gap-3 rounded-xl border border-brand-accent/30 bg-surface-raised px-4 py-3">
          <span className="text-sm font-medium text-text">{checked.size} selected</span>
          <span className="text-text-muted">—</span>
          <button
            onClick={() => setShowDeleteConfirm(true)}
            className="flex items-center gap-1.5 text-xs font-medium text-feedback-error transition-colors hover:text-feedback-error/80"
          >
            <Trash2 className="h-3.5 w-3.5" />Delete
          </button>
          <button
            onClick={() => exportToCSV(allPrompts.filter((p) => checked.has(p.query_text)))}
            className="flex items-center gap-1.5 text-xs font-medium text-text-muted transition-colors hover:text-text"
          >
            <Download className="h-3.5 w-3.5" />Export CSV
          </button>
          <button onClick={() => setChecked(new Set())} className="ml-auto text-text-muted hover:text-text">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Prompt list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <Spinner />
        </div>
      ) : filtered.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border py-16 text-center">
          <Search className="mx-auto mb-3 h-10 w-10 text-text-muted opacity-30" />
          <p className="font-medium text-text">
            {allPrompts.length === 0 ? 'No prompts yet' : 'No prompts match your filters'}
          </p>
          <p className="mt-1 text-sm text-text-muted">
            {allPrompts.length === 0
              ? 'Use the run bar below to simulate a query and get started.'
              : 'Try adjusting your filters.'}
          </p>
          {allPrompts.length === 0 && (
            <Button className="mt-4" onClick={() => setShowAdd(true)}>
              <Plus className="mr-2 h-4 w-4" />Add first prompt
            </Button>
          )}
        </div>
      ) : (
        <div className="space-y-2">
              {filtered.map((prompt) => (
            <PromptCard
              key={prompt.query_text}
              prompt={prompt}
              checked={checked.has(prompt.query_text)}
              onCheck={(c) => setChecked((prev) => { const next = new Set(prev); if (c) next.add(prompt.query_text); else next.delete(prompt.query_text); return next })}
              onRunAgain={(q) => handleRun(q, [...DEFAULT_PLATFORMS])}
              running={globalRunning}
              trackedBrands={trackedBrands}
              onTrack={handleTrack}
              tracking={tracking}
              brandName={brandName}
              onDelete={() => { setChecked(new Set([prompt.query_text])); setShowDeleteConfirm(true) }}
              websiteId={websiteId}
            />
          ))}
        </div>
      )}

      {showAdd && <AddPromptModal onClose={() => setShowAdd(false)} onAdded={() => void queryClient.invalidateQueries({ queryKey: ['simulate-results'] })} />}
      {showImport && <BulkImportModal onClose={() => setShowImport(false)} onImported={() => void queryClient.invalidateQueries({ queryKey: ['simulate-results'] })} />}
      {showDeleteConfirm && (
        <DeleteConfirmModal
          count={checked.size}
          onConfirm={handleDeleteSelected}
          onCancel={() => setShowDeleteConfirm(false)}
          deleting={deleteMutation.isPending}
        />
      )}

      {/* Sticky bottom run bar */}
      <div className="pointer-events-none fixed bottom-6 left-0 right-0 z-30 flex justify-center px-4 lg:left-60">
        <div className="pointer-events-auto w-full max-w-3xl">
          <RunBar
            onSubmit={handleRun}
            running={globalRunning}
            suggestedPrompts={activeWebsite?.suggestedPrompts ?? []}
            websiteId={websiteId}
            brandName={brandName}
          />
        </div>
      </div>
    </div>
  )
}
